// src/utils/dateUtils.ts
import { DateTime, DurationLikeObject } from 'luxon';
import { logger } from './logger';

/**
 * Common date formats used in the application
 */
export const DATE_FORMATS = {
  SHORT: 'MM/dd/yyyy',
  MEDIUM: 'LLL dd, yyyy',
  LONG: 'LLLL dd, yyyy',
  SHORT_WITH_TIME: 'LL/dd/yy HH:mm',
  MEDIUM_WITH_TIME: 'LLL dd, yyyy hh:mm a',
  LONG_WITH_TIME: 'LLLL dd, yyyy hh:mm a',
  ISO: 'yyyy-MM-dd',
  ISO_WITH_TIME: "yyyy-MM-dd'T'HH:mm:ss.SSS",
  ISO_WITH_TZ: "yyyy-MM-dd'T'HH:mm:ss.SSSZZZ",
  USER_FRIENDLY_WITH_TZ: 'LLL d yyyy, hh:mm:ss a ZZZZ',
};

/**
 * Date utility functions for consistent date handling across the application
 */
export const dateUtils = {
  /**
   * Format a date to a standard display format
   * @param date Date to format
   * @param format Format string using Luxon syntax (default: 'LLL dd, yyyy')
   * @returns Formatted date string
   */
  format(date: Date | string, format: string = DATE_FORMATS.MEDIUM): string {
    return DateTime.fromJSDate(this.toJSDate(date)).toFormat(format);
  },

  /**
   * Format a date to a localized format
   * @param date Date to format
   * @param locale Locale string (default: 'en-US')
   * @returns Localized date string
   */
  formatLocalized(date: Date | string, locale: string = 'en-US'): string {
    return DateTime.fromJSDate(this.toJSDate(date)).setLocale(locale).toLocaleString(DateTime.DATE_FULL);
  },

  /**
   * Convert a date to a different timezone
   * @param date Date to convert
   * @param timezone Target timezone (e.g., 'America/Los_Angeles')
   * @returns Date object in the specified timezone
   */
  toTimezone(date: Date | string, timezone: string): Date {
    return DateTime.fromJSDate(this.toJSDate(date)).setZone(timezone).toJSDate();
  },

  /**
   * Format a date with timezone conversion
   * @param date Date to format
   * @param timezone Target timezone
   * @param format Format string (default: 'LLL dd, yyyy')
   * @returns Formatted date string in the specified timezone
   */
  formatWithTimezone(date: Date | string, timezone: string, format: string = DATE_FORMATS.MEDIUM): string {
    return DateTime.fromJSDate(this.toJSDate(date)).setZone(timezone).toFormat(format);
  },

  /**
   * Get relative time (e.g., "5 minutes ago", "in 2 days")
   * @param date Date to compare with current time
   * @param baseDate Optional base date to compare against (defaults to current time)
   * @returns Relative time string
   */
  getRelativeTime(date: Date | string, baseDate?: Date | string): string {
    const dt = DateTime.fromJSDate(this.toJSDate(date));
    if (baseDate) {
      const base = DateTime.fromJSDate(this.toJSDate(baseDate));
      return dt.toRelative({ base }) || '';
    }
    return dt.toRelative() || '';
  },

  /**
   * Add time to a date
   * @param date Base date
   * @param duration Duration to add (e.g., {days: 2, hours: 3})
   * @returns New date with added time
   */
  add(date: Date | string, duration: DurationLikeObject): Date {
    return DateTime.fromJSDate(this.toJSDate(date)).plus(duration).toJSDate();
  },

  /**
   * Subtract time from a date
   * @param date Base date
   * @param duration Duration to subtract
   * @returns New date with subtracted time
   */
  subtract(date: Date | string, duration: DurationLikeObject): Date {
    return DateTime.fromJSDate(this.toJSDate(date)).minus(duration).toJSDate();
  },

  /**
   * Check if a date is valid
   * @param date Date to check
   * @returns Boolean indicating if date is valid
   */
  isValid(date: unknown): boolean {
    try {
      return DateTime.fromJSDate(this.toJSDate(date as Date | string)).isValid;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      return false;
    }
  },

  /**
   * Get the difference between two dates
   * @param date1 First date
   * @param date2 Second date
   * @param unit Unit of time for the result (default: 'days')
   * @returns Difference in the specified unit
   */
  diff(date1: Date | string, date2: Date | string, unit: keyof DurationLikeObject = 'days'): number {
    const dt1 = DateTime.fromJSDate(this.toJSDate(date1));
    const dt2 = DateTime.fromJSDate(this.toJSDate(date2));
    const diff = dt2.diff(dt1, [unit as keyof DurationLikeObject]);
    return diff.get(unit);
  },

  /**
   * Parse a date string with format
   * @param dateString String representation of a date
   * @param format Format of the date string
   * @returns Date object or null if invalid
   */
  parse(dateString: string, format: string): Date | null {
    const dt = DateTime.fromFormat(dateString, format);
    return dt.isValid ? dt.toJSDate() : null;
  },

  /**
   * Get current date and time in the specified timezone
   * @param timezone Target timezone (default: local timezone)
   * @returns Current date and time
   */
  now(timezone?: string): Date {
    return timezone ? DateTime.now().setZone(timezone).toJSDate() : DateTime.now().toJSDate();
  },

  /**
   * Get the start of a given time unit (day, week, month, year)
   * @param date Base date
   * @param unit Time unit ('day', 'week', 'month', 'year')
   * @returns Date representing the start of the time unit
   */
  startOf(date: Date | string, unit: 'day' | 'week' | 'month' | 'year'): Date {
    return DateTime.fromJSDate(this.toJSDate(date)).startOf(unit).toJSDate();
  },

  /**
   * Get the end of a given time unit (day, week, month, year)
   * @param date Base date
   * @param unit Time unit ('day', 'week', 'month', 'year')
   * @returns Date representing the end of the time unit
   */
  endOf(date: Date | string, unit: 'day' | 'week' | 'month' | 'year'): Date {
    return DateTime.fromJSDate(this.toJSDate(date)).endOf(unit).toJSDate();
  },

  /**
   * Convert various date inputs to standard JavaScript Date
   * @param date Input date (string, number, or Date)
   * @returns JavaScript Date object
   */
  toJSDate(date: Date | string | number): Date {
    if (date instanceof Date) {
      return date;
    }
    return new Date(date);
  },

  /**
   * Compare two dates
   * @param date1 First date
   * @param date2 Second date
   * @returns -1 if date1 < date2, 0 if equal, 1 if date1 > date2
   */
  compare(date1: Date | string, date2: Date | string): number {
    const d1 = this.toJSDate(date1).getTime();
    const d2 = this.toJSDate(date2).getTime();

    if (d1 < d2) return -1;
    if (d1 > d2) return 1;
    return 0;
  },

  /**
   * Check if two dates are the same day
   * @param date1 First date
   * @param date2 Second date
   * @returns Boolean indicating if dates are the same day
   */
  isSameDay(date1: Date | string, date2: Date | string): boolean {
    const d1 = DateTime.fromJSDate(this.toJSDate(date1));
    const d2 = DateTime.fromJSDate(this.toJSDate(date2));

    return d1.hasSame(d2, 'day');
  },

  /**
   * Format a UTC date to user's local timezone with user-friendly format
   * @param utcDate UTC date string or Date object
   * @returns Formatted date string in user's local timezone (e.g., "Sep 4th 2025, 07:57:17 PM PDT")
   */
  formatUTCToLocal(utcDate: string): string {
    const dt = DateTime.fromJSDate(this.toJSDate(utcDate), { zone: 'utc' }).toLocal();
    return dt.toFormat('FFF');
  },

  /**
   * Get list of common timezones
   * @returns Array of common timezone strings
   */
  getCommonTimezones(): string[] {
    return [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'Europe/London',
      'Europe/Paris',
      'Asia/Tokyo',
      'Australia/Sydney',
      'Pacific/Auckland',
    ];
  },

  /**
   * Parse a date string as a local date (no timezone conversion)
   * Handles both "2024-01-15" and "2024-01-15T00:00:00Z" formats
   *
   * @param dateString - Date string in YYYY-MM-DD format (with optional timestamp)
   * @returns DateTime object representing the local date
   * @throws Error if dateString is empty or invalid format
   */
  parseLocalDate(dateString: string): DateTime {
    if (!dateString) {
      throw new Error('Date string is required');
    }

    // Extract just the date part, ignoring any timestamp
    const dateOnly = dateString.split('T')[0];

    // Parse as local date (no timezone conversion) using strict parsing
    const parsedDate = DateTime.fromFormat(dateOnly, 'yyyy-MM-dd');

    if (!parsedDate.isValid) {
      throw new Error(`Invalid date format: ${dateString}. Expected YYYY-MM-DD format.`);
    }

    return parsedDate;
  },

  /**
   * Format a date string as a local date string without timezone conversion
   *
   * @param dateString - Date string in YYYY-MM-DD format (or null)
   * @param format - Optional Luxon format string (defaults to localized date format)
   * @returns Formatted date string or 'Not Set' if null, 'Invalid Date' if invalid
   */
  formatLocalDateString(dateString: string | null, format?: string): string {
    if (!dateString) {
      return 'Not Set';
    }

    try {
      const localDate = this.parseLocalDate(dateString);
      return format ? localDate.toFormat(format) : localDate.toLocaleString(DateTime.DATE_SHORT);
    } catch (error) {
      logger.error('Error formatting date:', { error });
      return 'Invalid Date';
    }
  },

  /**
   * Get timestamp for a date string without timezone conversion
   *
   * @param dateString - Date string in YYYY-MM-DD format
   * @returns Timestamp in milliseconds
   */
  getLocalDateTimestamp(dateString: string): number {
    const localDate = this.parseLocalDate(dateString);
    return localDate.toMillis();
  },

  /**
   * Calculate end date based on start date and product life months
   *
   * @param startDateString - Start date string in YYYY-MM-DD format
   * @param productLifeMonths - Number of months to add
   * @returns End date timestamp in milliseconds, or null if invalid
   */
  calculateEndDateTimestamp(startDateString: string, productLifeMonths: number | null): number | null {
    if (productLifeMonths === null || productLifeMonths === undefined) {
      return null;
    }

    // Convert to number in case API returns string
    const productLifeMonthsNum = Number(productLifeMonths);

    // Validate converted number - allow 0 as a valid value (immediate end date)
    if (isNaN(productLifeMonthsNum) || productLifeMonthsNum < 0) {
      logger.error(`Invalid productLifeMonths:', ${productLifeMonths}, '→', ${productLifeMonthsNum}`);
      return null;
    }

    try {
      const startDate = this.parseLocalDate(startDateString);

      // Add months using Luxon
      const endDate = startDate.plus({ months: productLifeMonthsNum });

      return endDate.toMillis();
    } catch (error) {
      logger.error('Error calculating end date:', { error });
      return null;
    }
  },

  /**
   * Get current timestamp in milliseconds
   * @returns Current timestamp in milliseconds
   */
  nowTimestamp(): number {
    return DateTime.now().toMillis();
  },

  /**
   * Format current date with specified format
   * @param format - Luxon format string (default: 'yyyy-MM-dd')
   * @returns Formatted current date string
   */
  formatNow(format: string = 'yyyy-MM-dd'): string {
    return DateTime.now().toFormat(format);
  },

  /**
   * Get current year
   * @returns Current year as number
   */
  currentYear(): number {
    return DateTime.now().year;
  },

  /**
   * Get year from timestamp
   * @param timestamp - Timestamp in milliseconds
   * @returns Year as number
   */
  yearFromTimestamp(timestamp: number): number {
    return DateTime.fromMillis(timestamp).year;
  },

  /**
   * Get month from timestamp (0-indexed for compatibility with moment)
   * @param timestamp - Timestamp in milliseconds
   * @returns Month as number (0-11)
   */
  monthFromTimestamp(timestamp: number): number {
    // Luxon uses 1-indexed months, subtract 1 for moment compatibility
    return DateTime.fromMillis(timestamp).month - 1;
  },

  /**
   * Create a DateTime for a specific date
   * @param year - Year
   * @param month - Month (0-indexed for compatibility with moment)
   * @param day - Day of month
   * @param hour - Hour (optional, default 0)
   * @param minute - Minute (optional, default 0)
   * @param second - Second (optional, default 0)
   * @returns Timestamp in milliseconds
   */
  createDateTimestamp(
    year: number,
    month: number,
    day: number,
    hour: number = 0,
    minute: number = 0,
    second: number = 0
  ): number {
    // month is 0-indexed for compatibility, Luxon uses 1-indexed
    return DateTime.local(year, month + 1, day, hour, minute, second).toMillis();
  },

  /**
   * Create a DateTime for a specific year, month, day and get timestamp
   * @param year - Year
   * @param quarterStartMonth - Month (0-indexed)
   * @param day - Day
   * @returns Timestamp in milliseconds
   */
  createQuarterDate(year: number, quarterStartMonth: number, day: number): number {
    // quarterStartMonth is 0-indexed, Luxon uses 1-indexed months
    return DateTime.local(year, quarterStartMonth + 1, day).toMillis();
  },

  /**
   * Get remaining time details from current time until a future date
   * @param futureDate Future date to calculate remaining time to
   * @param suffix Suffix to append to the time message (default: '')
   * @returns Human-readable time remaining message (e.g., "5 days remaining", "3 hours left")
   */
  getRemainingTimeDetails(futureDate: string | Date | number, suffix: string = ''): string {
    const currentDate = DateTime.now();
    const future = DateTime.fromJSDate(this.toJSDate(futureDate));
    const duration = future.diff(currentDate);
    return this.getTimeMessage(duration, suffix);
  },

  /**
   * Get elapsed time details from a past date until now
   * @param pastDate Past date to calculate elapsed time from
   * @returns Human-readable elapsed time message (e.g., "5 days ago", "3 hours ago")
   */
  getElapsedTimeDetails(pastDate: string | Date | number): string {
    const currentDate = DateTime.now();
    const past = DateTime.fromJSDate(this.toJSDate(pastDate));
    const duration = currentDate.diff(past);
    return this.getTimeMessage(duration, 'ago');
  },

  /**
   * Format a duration into a human-readable time message
   * @param duration Luxon Duration object
   * @param suffix Suffix to append to the message (e.g., 'ago', 'remaining', 'left')
   * @returns Formatted time message
   * @private
   */
  getTimeMessage(duration: ReturnType<DateTime['diff']>, suffix: string): string {
    let message = '';

    const days = Math.floor(duration.as('days'));
    const hours = Math.floor(duration.as('hours'));
    const minutes = Math.floor(duration.as('minutes'));

    switch (true) {
      case days >= 1:
        message = `${days} day${days > 1 ? 's' : ''} ${suffix}`;
        break;
      case hours >= 1:
        message = `${hours} hour${hours > 1 ? 's' : ''} ${suffix}`;
        break;
      case minutes >= 1:
        message = `${minutes} minute${minutes > 1 ? 's' : ''} ${suffix}`;
        break;
      default:
        message = `few seconds ${suffix}`;
        break;
    }

    return message.trim();
  },

  /**
   * Get relative time string following Cloudscape Design System timestamp patterns.
   *
   * Follows Cloudscape relative timestamp units:
   * - 0-59 seconds: "Now"
   * - 1-59 minutes: "# minutes ago"
   * - 1-24 hours: "# hours ago"
   * - 1-31 days: "# days ago"
   * - 1-4 weeks: "# weeks ago"
   * - 1-12 months: "# months ago"
   * - More than 12 months: "# years ago"
   *
   * @see https://core.cloudscape.aws.dev/patterns/general/timestamps/
   * @param date Date to compare with current time
   * @returns Cloudscape-compliant relative time string
   */
  getCloudscapeRelativeTime(date: Date | string): string {
    const now = DateTime.now();
    const dt = DateTime.fromJSDate(this.toJSDate(date));
    const diffInSeconds = Math.floor(now.diff(dt, 'seconds').seconds);

    // 0-59 seconds
    if (diffInSeconds < 60) {
      return 'Now';
    }

    // 1-59 minutes
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
    }

    // 1-24 hours
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
    }

    // 1-31 days
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 31) {
      return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
    }

    // 1-4 weeks
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks <= 4) {
      return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
    }

    // 1-12 months
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
      return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
    }

    // More than 12 months
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
  },
};
